/**
 * Simple centered spinner in the portal's accent color. Keeps `label`
 * and `message` as aliases so feature pages don't have to change calls.
 */

export interface LoaderProps {
  label?: string;
  /** Alias for `label`. Some feature code uses this spelling. */
  message?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const SIZE: Record<Required<LoaderProps>['size'], string> = {
  sm: 'w-4 h-4 border-[1.5px]',
  md: 'w-[22px] h-[22px] border-[1.5px]',
  lg: 'w-7 h-7 border-2',
};

export function Loader({ label, message, size = 'sm', className }: LoaderProps): JSX.Element {
  const text = label ?? message;
  return (
    <div
      role="status"
      aria-live="polite"
      className={['flex flex-col items-center justify-center gap-1.5 py-2', className]
        .filter(Boolean)
        .join(' ')}
    >
      <div
        className={[
          'animate-spin rounded-full border-[color:var(--gray-200)] border-t-[color:var(--accent)]',
          SIZE[size],
        ].join(' ')}
        aria-hidden="true"
      />
      {text ? (
        <span className="text-xs text-gray-500 font-[var(--font-body)]">
          {text}
        </span>
      ) : (
        <span className="sr-only">Loading</span>
      )}
    </div>
  );
}

export default Loader;
